import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { motion } from 'motion/react'

import { TMEntityLogoFactory } from '../../../icons/TickmillLogoIcons'
import { Link } from '../../../navigation/custom-react-router-dom'
import { ProductSwitch } from '../../../ui/ProductSwitch/ProductSwitch'
import { useAccountReadContext } from '../../../utils/AccountContextContext'
import { isTickmillUK } from '../../../utils/companyName.utils'
import { useBasePath } from '../../../utils/path'
import { useMenu } from '../../context/MenuContext'
import { useArabicSessionLanguage } from '../../context/SessionSettingsContext'
import { Guard } from '../../guard/Guard'
import { ActionsMenu, Menu } from '../Menu'
import { useMenuHover } from './useMenuHover'

import styles from './DesktopNav.module.scss'

interface Props {
  isAuthed?: boolean
}

export const DesktopNav: React.FC<Props> = ({ isAuthed }) => {
  const { redirectionLink } = useBasePath(true)
  const { setScreenMenuOpen, isMenuOpen } = useMenu()
  const isArabic = useArabicSessionLanguage()

  const { handlers } = useMenuHover()

  const { account } = useAccountReadContext()

  const [menuScrolled, setMenuScrolled] = useState(false)
  const handleMenuScroll: React.UIEventHandler<HTMLUListElement> = (e) => {
    setMenuScrolled(e.currentTarget.scrollTop > 0)
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setScreenMenuOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [setScreenMenuOpen])

  function handleCloseScreen() {
    setScreenMenuOpen(false)
  }

  return (
    <div
      id={'desktopNav'}
      className={classNames(styles.navWrapper, {
        [styles.open]: isMenuOpen,
        [styles.navWrapperRtl]: isArabic,
      })}
    >
      <div className={classNames(styles.overlay, { [styles.active]: isMenuOpen })} />

      <nav
        className={classNames(styles.nav, { [styles.open]: isMenuOpen })}
        {...handlers}
        role='navigation'
        aria-label='main navigation'
      >
        <div className={classNames(styles.navInner, { [styles.open]: isMenuOpen })}>
          <div className={classNames(styles.navLogo, { [styles.open]: isMenuOpen })}>
            <Link to={isAuthed ? redirectionLink : ''} onClick={handleCloseScreen}>
              <motion.div
                className={styles.logo}
                animate={isMenuOpen ? 'expanded' : 'collapsed'}
                initial='collapsed'
                transition={{ duration: 0.3, ease: 'easeInOut', type: 'tween' }}
                variants={{ expanded: { width: '210px' }, collapsed: { width: '42px' } }}
              >
                <TMEntityLogoFactory
                  className={classNames(styles.logoIcon, {
                    [styles.isMoved]: isArabic && !isMenuOpen,
                  })}
                />
              </motion.div>
            </Link>
          </div>

          <Guard condition={isAuthed && isTickmillUK(account)}>
            <div className={styles.productSwitch}>
              <ProductSwitch />
            </div>
          </Guard>

          <div className={styles.menuContainer}>
            <ActionsMenu showShadow={menuScrolled} />
            <Menu onScroll={handleMenuScroll} />
          </div>
        </div>
      </nav>
    </div>
  )
}
